import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, Search, User, MapPin, Truck, HeartPulse, ArrowRight, Store } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/shop", label: "Shop", testid: "nav-shop" },
  { to: "/shop/studio", label: "Custom Studio", testid: "nav-studio" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

const ANNOUNCEMENTS = [
  { Icon: MapPin, text: "Handmade in Memphis & Shipped Nationwide" },
  { Icon: Truck, text: "Fast Turnaround on Custom Orders" },
  { Icon: HeartPulse, text: "Personalized Gifts for Every Occasion" },
];

function Monogram() {
  return (
    <span className="relative inline-flex items-baseline metal-text font-serif leading-none select-none drop-shadow-[0_0_12px_rgba(0,140,255,0.25)]" aria-hidden="true">
      <span className="text-4xl sm:text-[42px]">C</span>
      <span className="text-4xl sm:text-[42px] -ml-2.5 sm:-ml-3">C</span>
    </span>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [annIdx, setAnnIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setAnnIdx((i) => (i + 1) % ANNOUNCEMENTS.length), 4000);
    return () => clearInterval(t);
  }, []);
  const { totals, setOpen: setCartOpen } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <>
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Metallic announcement bar */}
      <div className="champagne-bar text-chrome-bright" data-testid="announcement-bar">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-center">
          <div className="hidden md:flex items-center gap-6 text-[11px] font-medium tracking-wide">
            {ANNOUNCEMENTS.map(({ Icon, text }, i) => (
              <div key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-electric/40 mr-4">•</span>}
                <Icon size={13} className="text-electric" />
                <span className="text-chrome">{text}</span>
              </div>
            ))}
          </div>
          <div className="md:hidden flex items-center gap-2 text-[11px] font-medium text-chrome">
            <span key={annIdx} className="inline-flex items-center gap-2 animate-in fade-in duration-500">
              {(() => { const A = ANNOUNCEMENTS[annIdx]; const I = A.Icon; return <><I size={13} className="text-electric" />{A.text}</>; })()}
            </span>
          </div>
        </div>
      </div>

      {/* Main nav — deep navy metallic glass */}
      <div className="bg-navy-dark/95 backdrop-blur-md border-b border-electric/15">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="brand-logo">
            <Monogram />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-white text-xl sm:text-2xl tracking-tight">Collins Crafty <span className="hidden sm:inline">Creations</span></span>
              <span className="sm:hidden text-chrome/60 text-[9px] font-semibold tracking-[0.3em] mt-0.5">CREATIONS</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 text-[12px] font-semibold uppercase tracking-[0.18em] whitespace-nowrap transition-colors ${isActive ? "text-white" : "text-chrome/60 hover:text-white"}`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{item.label}</span>
                    {isActive && (
                      <motion.div layoutId="nav-underline" className="absolute left-3.5 right-3.5 -bottom-1 h-0.5 bg-electric shadow-[0_0_10px_rgba(0,140,255,0.7)]" transition={{ type: "spring", stiffness: 380, damping: 30 }} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => navigate("/shop")} data-testid="search-btn" className="p-2.5 rounded-full hover:bg-white/10 transition-colors text-chrome/80 hover:text-electric" aria-label="Search shop"><Search size={18} /></button>
            <button onClick={() => navigate("/account")} data-testid="account-btn" className="relative p-2.5 rounded-full hover:bg-white/10 transition-colors text-chrome/80 hover:text-electric" aria-label="My account" title={user ? user.name : "Sign in"}>
              <User size={18} />
              {user && <span className="absolute bottom-1 right-1 w-2 h-2 bg-emerald-400 rounded-full ring-2 ring-navy-dark" />}
            </button>
            <button onClick={() => setCartOpen(true)} data-testid="open-cart-btn" className="relative p-2.5 rounded-full hover:bg-white/10 transition-colors text-chrome/80 hover:text-electric" aria-label="Open cart">
              <ShoppingBag size={18} />
              <span data-testid="cart-badge" className="absolute -top-0.5 -right-0.5 bg-electric text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center shadow-[0_0_10px_rgba(0,140,255,0.6)]">{totals.count}</span>
            </button>
            <Link
              to="/shop/studio"
              data-testid="header-cta"
              className="group hidden md:inline-flex items-center gap-2 ml-1 rounded-full border border-electric/50 bg-electric/10 text-electric hover:bg-royal hover:text-white hover:border-royal px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors glow-blue-sm"
            >
              Custom Order <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="xl:hidden p-2 rounded-full hover:bg-white/10 text-white" data-testid="mobile-menu-btn" aria-label="Open menu">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="xl:hidden bg-navy-dark border-b border-electric/15 overflow-hidden" data-testid="mobile-menu">
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink key={item.label} to={item.to} end={item.to === "/"} onClick={() => setMobileOpen(false)} data-testid={`m-${item.testid}`}
                  className={({ isActive }) => `px-4 py-3 rounded-lg text-sm font-semibold ${isActive ? "bg-electric/15 text-electric" : "text-chrome/75"}`}>
                  {item.label}
                </NavLink>
              ))}
              <Link to="/account" onClick={() => setMobileOpen(false)} className="mt-2 px-4 py-3 rounded-md text-sm font-semibold bg-white/10 text-white text-center" data-testid="m-nav-account">{user ? "My Account" : "Sign In / Register"}</Link>
              <Link to="/shop/studio" onClick={() => setMobileOpen(false)} className="mt-1 px-4 py-3 rounded-full text-xs font-bold uppercase tracking-[0.15em] bg-royal text-white text-center glow-blue-sm" data-testid="m-nav-cta">Start a Custom Order</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>

    {/* Mobile sticky bottom bar (Nurfia-style) */}
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-navy-dark/95 backdrop-blur-md border-t border-electric/15 grid grid-cols-4" data-testid="mobile-bottom-bar">
      <Link to="/shop" onClick={() => setMobileOpen(false)} className="flex flex-col items-center gap-1 py-2.5 text-chrome/70 hover:text-electric transition-colors" data-testid="bb-store">
        <Store size={18} /><span className="text-[9px] uppercase tracking-[0.15em] font-semibold">Store</span>
      </Link>
      <button onClick={() => navigate("/shop")} className="flex flex-col items-center gap-1 py-2.5 text-chrome/70 hover:text-electric transition-colors" data-testid="bb-search">
        <Search size={18} /><span className="text-[9px] uppercase tracking-[0.15em] font-semibold">Search</span>
      </button>
      <Link to="/account" onClick={() => setMobileOpen(false)} className="flex flex-col items-center gap-1 py-2.5 text-chrome/70 hover:text-electric transition-colors" data-testid="bb-account">
        <User size={18} /><span className="text-[9px] uppercase tracking-[0.15em] font-semibold">Account</span>
      </Link>
      <button onClick={() => setCartOpen(true)} className="relative flex flex-col items-center gap-1 py-2.5 text-chrome/70 hover:text-electric transition-colors" data-testid="bb-cart">
        <ShoppingBag size={18} />
        {totals.count > 0 && <span className="absolute top-1 right-[22%] bg-electric text-white text-[8px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center">{totals.count}</span>}
        <span className="text-[9px] uppercase tracking-[0.15em] font-semibold">Cart</span>
      </button>
    </nav>
    </>
  );
}
